import { useEffect, useState } from "react";
import { PasswordLessLogin } from "../SignupPage/LoginStep/PasswordLessLogin";
import { trackEvent } from "@/utils/analytics";
import { AnalyticsEventType } from "@/constants/analytics";
import { LOGIN_OPTIONS } from "../SignupPage/LoginStep/LoginStep";
import { AuthMachineContext } from "@/store/machine/authMachine/authMachine";
import { SignupErrorFeedback } from "../SignupPage/SignupErrorFeedback/SignupErrorFeedback";
import { ucfirst } from "@/utils/string";
import { AuthEventType } from "@/types/auth";
import { cn } from "@/utils/styles";
import { Loader } from "../Common/Loader/Loader";
import { useLocation, useRoute } from "wouter";
import { Separator } from "../ui/separator";
import localCookies from "@/utils/cookie";

type LoginContentProps = {
    isOpen?: boolean;
    isSignUp?: boolean;
    onChangeIsSignUp?: (isSignUp: boolean) => void;
};

export const LoginContent = ({ isOpen, isSignUp, onChangeIsSignUp }: LoginContentProps) => {
    const [location] = useLocation();
    const [isSubmitting, setSubmitting] = useState(false);
    const [isOtpOpen, setIsOtpOpen] = useState(false);
    const [isDeprecatedOpen, setIsDeprecatedOpen] = useState(false);
    const authActorRef = AuthMachineContext.useActorRef();
    const failedError = AuthMachineContext.useSelector((state) => state.context.failedError);
    const ssoType = AuthMachineContext.useSelector((state) => state.context.ssoType);

    useEffect(() => {
        const pageVisibilityHandler = () => {
            setSubmitting(false);
        };

        window.addEventListener("pagehide", pageVisibilityHandler, false);
        return () => {
            window.removeEventListener("pagehide", pageVisibilityHandler);
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setSubmitting(false);
            return;
        }

        setIsOtpOpen(false);
    }, [isOpen]);

    return <div className="flex flex-col gap-7 md:max-w-[420px] p-7 pb-5 pt-0">
        {failedError && <SignupErrorFeedback>{ucfirst(failedError)}</SignupErrorFeedback>}

        <div className="flex flex-col">
            {!isOtpOpen && <div className="flex flex-col lg:grid lg:grid-cols-2 gap-2.5">
                {LOGIN_OPTIONS.map((option) => {
                    const { value, label, deprecated } = option;
                    const className = ['otp', 'other'].includes(value) ? "col-span-2" : "";

                    if (value === 'otp' && isOtpOpen) {
                        return;
                    }
                    if (!isDeprecatedOpen && deprecated) {
                        return;
                    }
                    if (value === 'other' && isDeprecatedOpen) {
                        return;
                    }

                    return (
                        <div className={cn("flex flex-col gap-2.5", className)}>
                            {value === 'otp' && <Separator className="flex flex-row items-center justify-center my-3">
                                <div className="flex flex-row items-center justify-center bg-black px-2 text-gray-dark-2200 text-sm">Or</div>
                            </Separator>}
                            <a
                                key={value}
                                className={cn(
                                    `group text-base hover:text-black !text-gray-dark-2200 bg-gray-dark-600 flex flex-row items-center justify-center gap-3 rounded border border-gray-dark-1100 px-3 py-[7px] text-white transition-all hover:border-white hover:bg-white hover:!text-black`,
                                    isSubmitting ? `pointer-events-none` : `cursor-pointer`
                                )}
                                onClick={async () => {
                                    if (value === "otp") {
                                        setIsOtpOpen(true);
                                        setIsDeprecatedOpen(false);
                                        return;
                                    }
                                    if (value === 'other') {
                                        setIsDeprecatedOpen(true);
                                        return;
                                    }

                                    trackEvent(
                                        isSignUp
                                            ? AnalyticsEventType.SIGN_UP_INITIATED
                                            : AnalyticsEventType.SIGNED_IN,
                                        {
                                            type: value
                                        }
                                    );
                                    window.dataLayer?.push({
                                        event: isSignUp
                                            ? AnalyticsEventType.SIGN_UP_INITIATED
                                            : AnalyticsEventType.SIGNED_IN,
                                        type: value
                                    });
                                    setSubmitting(true);

                                    localCookies.setItem("LOGIN_REDIRECT_ROUTE", location);

                                    window.setTimeout(() => {
                                        authActorRef.send({
                                            type: AuthEventType.SET_SSO_TYPE,
                                            value
                                        });
                                    }, 200);
                                }}
                            >
                                {option.icon && <option.icon className="text-white group-hover:text-black h-[20px]" />}
                                {value === 'otp' ? `${isSignUp ? "Sign up" : "Login"} with ${label}` : label}
                                {isSubmitting && value === ssoType && (
                                    <Loader className="!h-[14px] !w-[14px]" color="#fff" />
                                )}
                            </a>
                        </div>
                    );
                })}
            </div>}

            {isOtpOpen && <div className="flex flex-col gap-3 mt-3">
                <PasswordLessLogin
                    shouldRefreshOnSuccess={true}
                    hasBackButton={true}
                    onCancel={() => {
                        setIsOtpOpen(false);
                    }}
                />
            </div>}
        </div>

        {isSignUp ? (
            <p className="flex flex-row items-center text-sm text-gray-dark-2200">
                <span>Already a Member?</span>

                <div className="flex-1"></div>

                <a
                    onClick={() => {
                        onChangeIsSignUp?.(false);
                    }}
                    className="cursor-pointer text-white underline"
                >
                    Login
                </a>
            </p>
        ) : (
            <p className="flex flex-row items-center text-sm text-gray-dark-2200">
                <span>Don't have an account?</span>

                <div className="flex-1"></div>

                <a
                    onClick={() => {
                        trackEvent(AnalyticsEventType.SIGN_UP_CLICKED);
                        onChangeIsSignUp?.(true);
                    }}
                    className="cursor-pointer text-white underline"
                >
                    Sign Up
                </a>
            </p>
        )}
    </div>
};